import { Configuration, LogLevel } from "@azure/msal-browser"
import { env } from "env"

import { Client } from "api/client"

import { log, SHOW_LOGS } from "utilities/logging"

/**
 * Parse adb2cUrl to extract B2C configuration values
 * Example URL: https://creightonqab2c.b2clogin.com/creightonqab2c.onmicrosoft.com/oauth2/v2.0/authorize?p=B2C_1A_signup&client_id=14b14a7b-891a-4f36-9818-514508386506&nonce=defaultNonce&scope=openid&response_type=id_token&prompt=login
 */
export interface MsalConfigValues {
  tenantDomain: string // e.g., "creightonqab2c.b2clogin.com"
  tenantName: string // e.g., "creightonqab2c.onmicrosoft.com"
  policy: string // e.g., "B2C_1A_signup"
  clientId: string // e.g., "12a34b5c-678d-9r01-2345-67890abcdefg"
  authority: string // e.g., "https://creightonqab2c.b2clogin.com/creightonqab2c.onmicrosoft.com/B2C_1A_signup"
}

/**
 * Extract MSAL configuration values from client's adb2cUrl
 */
export const parseMsalConfig = (client: Client): MsalConfigValues => {
  if (!client.adb2cUrl) {
    throw new Error("Client adb2cUrl is required for MSAL configuration")
  }

  const url = new URL(client.adb2cUrl)
  const params = new URLSearchParams(url.search)

  // Extract policy from query parameter (e.g., "B2C_1A_SignUp")
  const policy = params.get("p") || "B2C_1A_SignUp"

  // Extract client ID from query parameter
  const clientId = params.get("client_id")
  if (!clientId) {
    throw new Error("client_id not found in adb2cUrl")
  }

  // Extract tenant domain from hostname (e.g., "creightonqab2c.b2clogin.com")
  const tenantDomain = url.hostname

  // Extract tenant name from pathname (e.g., "/creightonqab2c.onmicrosoft.com/oauth2/v2.0/authorize")
  // Path format: /{tenant}.onmicrosoft.com/oauth2/v2.0/authorize
  const pathParts = url.pathname.split("/")
  const tenantName = pathParts[1] // e.g., "creightonqab2c.onmicrosoft.com"

  if (!tenantName || !tenantName.includes(".onmicrosoft.com")) {
    throw new Error(
      `Invalid tenant name format in adb2cUrl: ${url.pathname}. Expected format: /{tenant}.onmicrosoft.com/oauth2/v2.0/authorize`,
    )
  }

  // Build MSAL authority URL
  // Format: https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/{policy}
  const authority = `https://${tenantDomain}/${tenantName}/${policy}`

  return {
    tenantDomain,
    tenantName,
    policy,
    clientId,
    authority,
  }
}

/**
 * Convert authority URL to use SignIn policy (sign-in only, no sign-up option)
 * Handles various policy name formats (B2C_1A_signup, B2C_1A_SignUp, etc.)
 * Use this when you need to direct users to sign-in only.
 */
export const getSignInAuthority = (authority: string): string => {
  // Replace any policy with signin policy
  // Handles: B2C_1A_signup, B2C_1A_SignUp, B2C_1A_SIGNUP, b2c_1a_signup, etc.
  return authority.replace(/B2C_1A_[^/]+$/i, "B2C_1A_SignIn")
}

/**
 * Convert authority URL to use SignUp policy
 * Handles various policy name formats
 */
export const getSignUpAuthority = (authority: string): string => {
  // Replace any policy with signup policy
  return authority.replace(/B2C_1A_[^/]+$/i, "B2C_1A_SignUp")
}

/**
 * Create MSAL configuration object from client data
 */
export const createMsalConfig = (client: Client): Configuration => {
  const configValues = parseMsalConfig(client)

  return {
    auth: {
      clientId: configValues.clientId,
      authority: configValues.authority,
      knownAuthorities: [configValues.tenantDomain],
      // In dev mode, always use localhost. In production, use backend's redirectUrl
      // This ensures local development works correctly
      redirectUri: env.DEV
        ? window.location.origin
        : client.redirectUrl || window.location.origin,
      postLogoutRedirectUri: env.DEV
        ? `${window.location.origin}/logged-out`
        : `${client.redirectUrl || window.location.origin}/logged-out`,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      allowRedirectInIframe: false, // prevents CSP errors
      loggerOptions: {
        loggerCallback: (level, message) => {
          // Only log errors in production, all logs in development
          if (level === LogLevel.Error) {
            console.error(`[MSAL] ${message}`)
          } else {
            log(SHOW_LOGS.MSAL, `[MSAL] ${message}`)
          }
        },
        logLevel: env.DEV ? LogLevel.Verbose : LogLevel.Error,
      },
    },
  }
}
